'use client'

import React, { useEffect, useId, useRef, useState } from 'react'

import { cn } from './cn'

interface DropdownTriggerProps {
  'aria-controls': string
  'aria-expanded': boolean
  'aria-haspopup': true
  onClick: () => void
}

interface DropdownContentProps {
  id: string
}

interface DropdownProps {
  className?: string
  id?: string
  renderContent: (props: {
    close: () => void
    contentProps: DropdownContentProps
  }) => React.ReactNode
  renderTrigger: (props: { isOpen: boolean; triggerProps: DropdownTriggerProps }) => React.ReactNode
}

export const Dropdown = ({
  className,
  id,
  renderContent,
  renderTrigger,
}: DropdownProps): React.ReactElement => {
  const generatedId = useId()
  const contentId = id ?? generatedId
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const close = (): void => setIsOpen(false)

  useEffect(() => {
    if (!isOpen) return

    const handlePointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && !dropdownRef.current?.contains(event.target)) {
        close()
      }
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div ref={dropdownRef} className={cn('snplay:relative', className)}>
      {renderTrigger({
        isOpen,
        triggerProps: {
          'aria-controls': contentId,
          'aria-expanded': isOpen,
          'aria-haspopup': true,
          onClick: () => setIsOpen((v) => !v),
        },
      })}
      {isOpen && renderContent({ close, contentProps: { id: contentId } })}
    </div>
  )
}
